import { HotelSearchResult, HotelDetailsResult } from '@reward-platform/ancillaries-schemas/hotel'
import {
  getPromotionsForCardDisplay,
  PriceLadder,
  RoomRateOption,
} from '@reward-platform/ancillaries-schemas/common'
import { Promotion } from '@reward-platform/shared-schemas'

export type ExtractedHotelPricing = {
  cashPrice: number
  cashPriceWithSurcharges?: number
  aviosPrice: number
  collectAmount: number
  baseAmount?: number
  pricingLadder?: PriceLadder[]
  promotions?: Promotion[]
  surcharges?: number
  baseLoyalty?: number
}

const getRoomRatePromotions = (roomRate?: RoomRateOption): Promotion[] | undefined => {
  if (!roomRate?.paymentOptions) return roomRate?.promotions
  return getPromotionsForCardDisplay(roomRate.paymentOptions) as Promotion[] | undefined
}

/**
 * Extracts pricing from hotel room rates.
 * Returns the lowest available room rate pricing.
 */
export const getHotelPricingByLowestRoomRate = (
  hotel: HotelSearchResult | HotelDetailsResult
): ExtractedHotelPricing => {
  const allRates = hotel.rooms.flatMap((room) => room.roomRates).filter((rate) => rate.available)

  if (allRates.length === 0) {
    // Fallback if no available rates
    const firstRate = hotel.rooms[0]?.roomRates[0]
    return {
      cashPrice: firstRate?.cashPrice ?? 0,
      cashPriceWithSurcharges: (firstRate?.cashPrice ?? 0) + (hotel.surcharges ?? 0),
      aviosPrice: firstRate?.aviosPrice ?? 0,
      collectAmount: firstRate?.collectAmount ?? 0,
      baseAmount: firstRate?.baseAmount,
      pricingLadder: firstRate?.pricingLadder?.map((item) => ({
        ...item,
        cashPriceWithSurcharges: item.cashPrice + (hotel.surcharges ?? 0),
      })),
      promotions: getRoomRatePromotions(firstRate),
      surcharges: hotel.surcharges,
      baseLoyalty: firstRate?.baseLoyalty,
    }
  }

  // Find the rate with the lowest cash price
  const lowestRate = allRates.reduce((lowest: RoomRateOption, current: RoomRateOption) => {
    return current.cashPrice < lowest.cashPrice ? current : lowest
  })

  return {
    cashPrice: lowestRate.cashPrice,
    cashPriceWithSurcharges: lowestRate.cashPrice + (hotel.surcharges ?? 0),
    aviosPrice: lowestRate.aviosPrice,
    collectAmount: lowestRate.collectAmount,
    baseAmount: lowestRate.baseAmount,
    pricingLadder: lowestRate.pricingLadder?.map((item) => ({
      ...item,
      cashPriceWithSurcharges: item.cashPrice + (hotel.surcharges ?? 0),
    })),
    promotions: getRoomRatePromotions(lowestRate),
    surcharges: hotel.surcharges,
    baseLoyalty: lowestRate.baseLoyalty,
  }
}
